/**
 * Registry for SMS-linked fee payment pages (API: GET/PUT/POST .../parcels/payments/:page/...).
 * To add a new fee type:
 * 1. Add an entry here (publicPath must match the first URL segment from SMS, e.g. paySplitFee).
 * 2. Add matching key + numeric value in helpers/payments.js PaymentPageOpts (for CreditGuard callback validation).
 * 3. Add API entries in Redux api.reducer.js (generic keys use :page: + :id:).
 * No new route lines are required if publicPath follows /{publicPath}/:parcelID — Routes.js maps this registry.
 */

import React from 'react';

/** Prefer hawb for display; fallback to API `xlt` (parcel id shown when no HAWB). */
export function resolveSmsFeeTrackingRef(data) {
  if (!data) return '';
  const h = data.hawb;
  const x = data.xlt != null ? data.xlt : data.xltID;
  if (h != null && String(h).trim()) return String(h).trim();
  if (x != null && String(x).trim()) return String(x).trim();
  return '';
}

/** Whole hours for UI (avoids 67.17 שעות). */
export function formatHoursLeftForDisplay(hours) {
  if (hours === null || hours === undefined || Number.isNaN(Number(hours))) return '';
  const n = Number(hours);
  return String(Math.max(0, Math.round(n)));
}

export function formatPriceShekels(price) {
  if (price === null || price === undefined || Number.isNaN(Number(price))) return '';
  const n = Number(price);
  if (Number.isInteger(n)) return String(n);
  return n.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export const SMS_FEE_PAYMENT_PAGES = {
  paySplitFee: {
    /** Same as API `payments/:page` segment */
    apiPageKey: 'paySplitFee',
    /** First path segment on this site, must match config `paySplitFeeSMSUrlInfo` / SMS link */
    publicPath: 'paySplitFee',
    /** Key on PaymentPageOpts */
    paymentPageOptsKey: 'paySplitFee',
    copy: {
      formIntro: ({ trackingRef, priceDisplay, hoursDisplay }) => (
        <>
          <strong>תשלום עמלת פיצול (מכס)</strong>
          <br />
          <br />
          עבור משלוח <strong>{trackingRef}</strong> נדרש תשלום בגין טיפול מכסי בפיצול חבילה (מוצרים אסורים מול מותרים), לפי דרישת הרשויות.
          <br />
          <br />
          סכום לתשלום: <strong>{priceDisplay} ש&quot;ח</strong> (כולל מע&quot;מ).
          <br />
          ניתן לשלם בצורה מאובטחת בשלב הבא. תוקף ההצעה: עוד <strong>{hoursDisplay}</strong> שעות.
        </>
      ),
      expiredTitle: (
        <>
          פג תוקף חלון התשלום לעמלת הפיצול.
          <br />
          <span style={{ fontWeight: 'normal' }}>לסיוע נוסף פנו לשירות הלקוחות.</span>
        </>
      ),
      successTitle: 'התשלום הושלם בהצלחה',
      successBody: ({ trackingRef }) => (
        <p style={{ marginTop: '25px' }}>
          <strong>מס&apos; למעקב: </strong>
          <span>&nbsp;{trackingRef}</span>
        </p>
      ),
      errorPrefix: 'אירעה שגיאה בעת ביצוע התשלום',
      cancelledTitle: 'התשלום בוטל.',
    },
  },
};

export function getSmsFeePaymentConfigByPublicPath(publicPath) {
  return Object.values(SMS_FEE_PAYMENT_PAGES).find((p) => p.publicPath === publicPath);
}

/** pathname e.g. /paySplitFee/callback/success → paySplitFee */
export function getSmsFeePublicPathFromCallbackPathname(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length >= 2 && parts[1] === 'callback') {
    return parts[0];
  }
  return null;
}
